import React, {useCallback, useState} from "react";
import PropTypes from "prop-types";
import { Dropdown, DropdownButton } from "react-bootstrap";
import {toast} from "react-toastify";
import { inject, observer } from "mobx-react";

import { checkOfflineState } from "../OfflineDetect";
import Analytics from "../../lib/Analytics";

/**
 * NavBarIconLabel
 * Stacks a glyphicon above a small ALL-CAPS text label.
 */
const NavBarIconLabel = ({ icon, label, busy = false }) => (
  <span className="navbar-icon-label">
    <span className={'glyphicon ' + icon + (busy ? ' icon-pulse' : '')} aria-hidden="true" />
    <span className="navbar-icon-label__text">{label}</span>
  </span>
);

const ChatDownloadMenu = ({
                          className,
                          disabled = false,
                          client = null,
                          conversationId = null,
                          searchParams = {},
                          editedTemplates = {},
                          conversationContext = null,
                          conversationTitle = null,
                          aiTemplates = {},
                          dsConfig = null,
                          extractConversationText = null,
                          includeToolUse = false,
                          userStore: {isAdmin},
                        }) => {

  const [isDownloadingDOCX, setIsDownloadingDOCX] = useState(false);
  const [isDownloadingPPTX, setIsDownloadingPPTX] = useState(false);
  const [isDownloadingPlex, setIsDownloadingPlex] = useState(false);
  const [isDownloadingText, setIsDownloadingText] = useState(false);

  const busy = isDownloadingDOCX || isDownloadingPPTX || isDownloadingPlex || isDownloadingText;

  const handleDownloadPlex = useCallback(async () => {
    if (!client) return;
    Analytics.event(Analytics.EVENTS.CHAT_EXPORT, { conversation_id: conversationId, format: "plex" });
    setIsDownloadingPlex(true);
    try {
      await client.downloadConversation({
        asJSON: true,
        asPlex: true,  // Use .plex extension instead of .json
        searchParams,
        editedTemplates,
        conversationContext,
        conversationTitle
      });
    } catch (error) {
      toast.error(`Failed to download conversation: ${error.message}`);
    } finally {
      setIsDownloadingPlex(false);
    }
  }, [client, searchParams, editedTemplates, conversationContext, conversationTitle]);

  const handleDownloadText = useCallback(async () => {
    if (!client || !extractConversationText) return;
    Analytics.event(Analytics.EVENTS.CHAT_EXPORT, { conversation_id: conversationId, format: "text" });
    setIsDownloadingText(true);
    try {
      await client.downloadConversation({
        asJSON: false,
        searchParams,
        editedTemplates,
        conversationContext,
        conversationTitle,
        dsConfig,
        extractConversationText,
        includeToolUse
      });
    } catch (error) {
      toast.error(`Failed to download text: ${error.message}`);
    } finally {
      setIsDownloadingText(false);
    }
  }, [client, searchParams, editedTemplates, conversationContext, conversationTitle, dsConfig, extractConversationText, includeToolUse]);

  const handleDownloadDOCX = useCallback(() => {
    if (!client) return;
    Analytics.event(Analytics.EVENTS.CHAT_EXPORT, { conversation_id: conversationId, format: "docx" });
    client.downloadSummary({
      outputFormat: "docx",
      verbose: false,
      searchParams,
      editedTemplates,
      conversationContext,
      conversationTitle,
      aiTemplates,
      onStart: () => setIsDownloadingDOCX(true),
      onComplete: () => setIsDownloadingDOCX(false),
      onError: (format, error) => {
        setIsDownloadingDOCX(false);
        const msg = error.name === 'OneshotTimeoutError'
          ? "Report generation timed out. The conversation may be too long — try again. Plex support has been notified."
          : `Failed to generate report: ${error.message}`;
        toast.error(msg);
      }
    });
  }, [client, searchParams, editedTemplates, conversationContext, conversationTitle, aiTemplates]);

  const handleDownloadPPTX = useCallback(() => {
    if (!client) return;
    Analytics.event(Analytics.EVENTS.CHAT_EXPORT, { conversation_id: conversationId, format: "pptx" });
    client.downloadSummary({
      outputFormat: "pptx",
      verbose: false,
      searchParams,
      editedTemplates,
      conversationContext,
      conversationTitle,
      aiTemplates,
      onStart: () => setIsDownloadingPPTX(true),
      onComplete: () => setIsDownloadingPPTX(false),
      onError: (format, error) => {
        setIsDownloadingPPTX(false);
        const msg = error.name === 'OneshotTimeoutError'
          ? "Presentation generation timed out. The conversation may be too long — try again. Plex support has been notified."
          : `Failed to generate presentation: ${error.message}`;
        toast.error(msg);
      }
    });
  }, [client, searchParams, editedTemplates, conversationContext, conversationTitle, aiTemplates]);

  return (
      <span title={"Save this conversation"} className="export-menu">
        <DropdownButton
          id="dropdown-chat-download-menu"
          key="chat-download"
          disabled={disabled}
          className={className}
          title={<NavBarIconLabel icon="glyphicon-download-alt" label="Save" busy={busy} />}
        >
          <Dropdown.Item
            disabled={disabled || isDownloadingPlex}
            title={"Download conversation as .plex file"}
            onClick={checkOfflineState(handleDownloadPlex)}
            className="export-dropdown-item"
          >
            <span className={`glyphicon glyphicon-save export-icon${isDownloadingPlex ? " icon-pulse" : ""}`} />
            <span>{isDownloadingPlex ? "Preparing download..." : "Download Conversation"}</span>
          </Dropdown.Item>
          <Dropdown.Item
            disabled={disabled || isDownloadingText}
            title={"Download a text representation of this conversation"}
            onClick={checkOfflineState(handleDownloadText)}
            className="export-dropdown-item"
          >
            <span className={`download-txt-icon export-icon${isDownloadingText ? " icon-pulse" : ""}`} />
            <span>{isDownloadingText ? "Preparing download..." : "Download Conversation as Text"}</span>
          </Dropdown.Item>
          <Dropdown.Divider />
          <Dropdown.Item
            disabled={disabled || isDownloadingDOCX}
            title={"Generate a report summarizing this conversation"}
            onClick={checkOfflineState(handleDownloadDOCX)}
            className="export-dropdown-item"
          >
            <span className={`download-doc-icon export-icon${isDownloadingDOCX ? " icon-pulse" : ""}`} />
            <span>{isDownloadingDOCX ? "Generating report..." : "Generate Report"}</span>
          </Dropdown.Item>
          <Dropdown.Item
            disabled={disabled || isDownloadingPPTX}
            title={"Generate a presentation summarizing this conversation"}
            onClick={checkOfflineState(handleDownloadPPTX)}
            className="export-dropdown-item"
          >
            <span className={`download-ppt-icon export-icon${isDownloadingPPTX ? " icon-pulse" : ""}`} />
            <span>{isDownloadingPPTX ? "Generating presentation..." : "Generate Presentation"}</span>
          </Dropdown.Item>
        </DropdownButton>
      </span>
    );
};

ChatDownloadMenu.propTypes = {
  className: PropTypes.string,
};

export default inject("userStore")(observer(ChatDownloadMenu));
